import { useMemo } from 'react';
import { useLocation } from 'react-router';

import {
  Icon,
  RyeconChartLine,
  RyeconLightbulb,
  RyeconList,
} from '@dbt-labs/sourdough';

import { useCurrentPage } from '@/components/Page';
import { PageLayout } from '@/components/PageLayout';
import { DbtCloudProject, useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useDevelopmentContext } from '@/context/DevelopmentContextProvider';
import { useAdminDevStagingProdEnvs } from '@/hooks/admin/environments';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams, useValidatedParams } from '@/hooks/params';
import { MockDbtCloudProject } from '@/MockDbtCloudProject';
import { isDevelopment } from '@/typings/url';
import { DatabaseFilter } from './DatabaseFilter/DatabaseFilter';
import { FileTreeFilter } from './FileTreeFilter/FileTreeFilter';
import { NavButtonOption, NavButtonOptionParams } from './NavButton/NavButtonGroup';
import { PanelHeader } from './PanelHeader';
import { usePanelView } from './panelView';
import { PanelViewSwitcher } from './PanelViewSwitcher';
import { ResourcesFilter } from './ResourcesFilter/ResourcesFilter';

interface PanelHeaderEnvironment {
  id: number;
}
interface PanelHeaderEnvironmentsByType {
  prod: PanelHeaderEnvironment | null;
  staging: PanelHeaderEnvironment | null;
  dev: PanelHeaderEnvironment | null;
}

export const ProjectNavPanel = () => {
  const { environmentType } = useValidatedParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const originProject = searchParams.get('originProject');
  const projectScope = useUrlProjectParams();
  const { currentPage } = useCurrentPage();
  const [navMode, updateNavMode] = usePanelView();
  const { accountId, projectName, projectId } = useDbtCloudProject();
  const devData = useDevelopmentContext();
  const { dbtCloudProjects, hasAccountFeature, account } = useDbtCloudAccount();
  const adminEnvironments = useAdminDevStagingProdEnvs({
    accountId,
    projectId,
  }).data;
  const overviewUrl = useLinkToExplorer('project')(projectScope);
  const performanceUrl = useLinkToExplorer('performance')(projectScope);
  const recommendationsUrl = useLinkToExplorer('recommendations')(projectScope);

  const environments = useMemo((): PanelHeaderEnvironmentsByType => {
    if (adminEnvironments) {
      return {
        prod: adminEnvironments?.production?.id
          ? { id: adminEnvironments.production.id }
          : null,
        staging: adminEnvironments?.staging?.id
          ? { id: adminEnvironments.staging.id }
          : null,
        dev: adminEnvironments?.development?.id
          ? { id: adminEnvironments.development.id }
          : null,
      };
    }

    if (devData.shouldMockProjects) {
      const cloudProject = dbtCloudProjects?.find((p) => p.id === projectId) as
        | (MockDbtCloudProject & DbtCloudProject)
        | undefined;
      return {
        prod: cloudProject?.productionEnvironmentId
          ? { id: cloudProject.productionEnvironmentId }
          : null,
        staging: cloudProject?.stagingEnvironmentId
          ? { id: cloudProject.stagingEnvironmentId }
          : null,
        dev: cloudProject?.devEnvironmentId
          ? { id: cloudProject.devEnvironmentId }
          : null,
      };
    }

    return { prod: null, staging: null, dev: null };
  }, [adminEnvironments, dbtCloudProjects, devData, projectId]);

  const options = useMemo<NavButtonOptionParams<string>[]>(() => {
    const items: NavButtonOptionParams<string>[] = [
      {
        to: overviewUrl,
        config: {
          option: 'projectOverview',
          icon: <Icon className="mx-auto text-current" ryecon={RyeconList} />,
          label: 'Overview',
        },
      },
      ...(isDevelopment(environmentType) ||
      !hasAccountFeature('explorerModelPerformance')
        ? []
        : [
            {
              to: performanceUrl,
              config: {
                option: 'performance',
                icon: (
                  <Icon className="mx-auto text-current" ryecon={RyeconChartLine} />
                ),
                label: 'Performance',
              },
            },
          ]),
      ...(hasAccountFeature('explorerRecommendations')
        ? [
            {
              to: recommendationsUrl,
              config: {
                option: 'recommendations',
                icon: (
                  <Icon className="mx-auto text-current" ryecon={RyeconLightbulb} />
                ),
                label: 'Recommendations',
              },
            },
          ]
        : []),
    ];
    return items;
  }, [
    environmentType,
    hasAccountFeature,
    overviewUrl,
    performanceUrl,
    recommendationsUrl,
  ]);

  if (!accountId) {
    return <>Invalid account ID</>;
  }
  if (!projectScope) {
    return <>Invalid URL</>;
  }
  return (
    <PageLayout isLoading={!account}>
      <div className="h-full w-full">
        <PanelHeader
          projectName={projectName}
          productionEnv={environments?.prod?.id ?? 0}
          stagingEnv={environments?.staging?.id}
          devEnv={environments?.dev?.id}
          className="flex h-full min-h-0 w-full flex-col items-stretch"
        >
          <div>
            <div>{originProject}</div>
            <div className="mb-2 text-sm text-fgMain">Project details</div>
            {options.map((opt) => (
              <NavButtonOption
                {...opt}
                key={opt.config.option}
                currentOption={currentPage}
                className="mb-2"
                isBeta={opt.config.isBeta}
              />
            ))}
          </div>
          <PanelViewSwitcher navMode={navMode} updateNavMode={updateNavMode} />
          <div className="h-full flex-1">
            {navMode === 'fileTree' ? (
              <FileTreeFilter />
            ) : (
              <>
                {navMode === 'resources' && <ResourcesFilter />}
                {navMode === 'database' && <DatabaseFilter />}
              </>
            )}
          </div>
        </PanelHeader>
      </div>
    </PageLayout>
  );
};
